import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { XIcon, PencilIcon } from "lucide-react";
import { AdminMarathonPayment } from "../../types/marathonAdmin";
import { useLazyGetUserAccountsAndTicketsQuery } from "../../api/marathonAdmin";
import { MarathonAccountEditModal } from "./MarathonAccountEditModal";
import { MarathonTicketEditModal } from "./MarathonTicketEditModal";

interface MarathonUserAccountsModalProps {
    isOpen: boolean;
    onClose: () => void;
    marathonId: number;
    participantId: number;
    userId: number;
    userName: string;
}

export const MarathonUserAccountsModal: React.FC<
    MarathonUserAccountsModalProps
> = ({ isOpen, onClose, marathonId, participantId, userId, userName }) => {
    const { t } = useTranslation();
    const [getUserAccountsAndTickets, { isFetching: isLoading }] =
        useLazyGetUserAccountsAndTicketsQuery();
    const [accounts, setAccounts] = useState<any[]>([]);
    const [tickets, setTickets] = useState<any[]>([]);
    const [payments, setPayments] = useState<AdminMarathonPayment[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [editingAccount, setEditingAccount] = useState<any>(null);
    const [editingTicket, setEditingTicket] = useState<any>(null);

    const fetchData = useCallback(async () => {
        try {
            setError(null);
            const response = await getUserAccountsAndTickets({
                marathonId,
                participantId,
                userId,
            }).unwrap();
            
            if (response.success && response.data) {
                setAccounts(response.data.accounts || []);
                setTickets(response.data.tickets || []);
                setPayments(response.data.payments || []);
            } else {
                setError(t("common:error_occurred", "Произошла ошибка при загрузке данных"));
            }
        } catch (err: any) {
            setError(err?.data?.message || t("common:error_occurred", "Произошла ошибка при загрузке данных"));
        }
    }, [marathonId, participantId, userId, getUserAccountsAndTickets, t]);

    useEffect(() => {
        if (isOpen && marathonId && participantId && userId) {
            fetchData();
        }
    }, [isOpen, marathonId, participantId, userId, fetchData]);

    const formatDate = (time: number | string | null | undefined) => {
        if (!time) return "—";
        // Если это число или строка, состоящая только из цифр, считаем это UNIX timestamp (в секундах)
        if (typeof time === "number" || /^\d+$/.test(String(time))) {
            return new Date(Number(time) * 1000).toLocaleString();
        }
        // Иначе считаем это ISO строкой или другим форматом, который поймет конструктор Date
        return new Date(time).toLocaleString();
    };

    if (!isOpen) return null;

    const renderStatusBadge = (statusLabel: string) => {
        let colorClass = "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
        if (statusLabel?.toLowerCase().includes("актив")) {
            colorClass = "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
        } else if (statusLabel?.toLowerCase().includes("ожида")) {
            colorClass = "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
        } else if (statusLabel?.toLowerCase().includes("удал") || statusLabel?.toLowerCase().includes("отменен")) {
            colorClass = "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
        }

        return (
            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
                {statusLabel}
            </span>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-[90vh] pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                ></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>

                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                                Аккаунты, билеты, платежи пользователя: {userName}
                            </h3>
                            <button
                                onClick={onClose}
                                className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                <span className="sr-only">Закрыть</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        
                        {isLoading ? (
                            <div className="flex justify-center p-8">
                                <div className="text-gray-500 dark:text-gray-400">
                                    {t("common:loading", "Загрузка...")}
                                </div>
                            </div>
                        ) : error ? (
                            <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 p-4 rounded-md text-sm">
                                {error}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Исходные аккаунты */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 tracking-wide">
                                        Аккаунты в марафоне ({accounts.length})
                                    </h4>
                                    {accounts.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Нет аккаунтов</p>
                                    ) : (
                                        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden border-b border-gray-200 dark:border-gray-700 sm:rounded-lg">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Дата вступления</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Дата оплаты</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Статус</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Платит за / Кто платит</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Приз</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Возврат (ID / Сумма)</th>
                                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Действия</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                        {accounts.map((account) => (
                                                            <tr key={account.alny_marafon_people_id}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{account.alny_marafon_people_id}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {formatDate(account.alny_marafon_people_time)}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {formatDate(account.alny_marafon_people_fee)}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{renderStatusBadge(account.status_label || (account.alny_marafon_people_status === 0 ? "Участвует" : "Отменен"))}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {account.alny_marafon_people_pays || "—"} / {account.alny_marafon_people_pay || "—"}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {account.alny_marafon_people_reward === 1 ? <span className="text-green-600 font-bold">Да</span> : "Нет"}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {account.alny_marafon_people_place || "—"} / {account.alny_marafon_people_amount || 0}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                    <button
                                                                        onClick={() => setEditingAccount(account)}
                                                                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                                                                        title="Редактировать"
                                                                    >
                                                                        <PencilIcon className="h-4 w-4" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Билеты */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 tracking-wide">
                                        Билеты пользователя ({tickets.length})
                                    </h4>
                                    {tickets.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Нет билетов</p>
                                    ) : (
                                        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden border-b border-gray-200 dark:border-gray-700 sm:rounded-lg">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID / Участник</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Статус</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Создан</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Оплата</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">За кого (Участник / Билет)</th>
                                                            <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Действия</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                        {tickets.map((ticket) => (
                                                            <tr key={ticket.alny_marafone_tickets_id}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {ticket.alny_marafone_tickets_id} / <span className="font-semibold">{ticket.alny_marafone_tickets_choosen_id || "—"}</span>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {renderStatusBadge(
                                                                        ticket.status_label || 
                                                                        (ticket.alny_marafone_tickets_status === 0 ? "Рабочий" : 
                                                                         ticket.alny_marafone_tickets_status === 1 ? "Победил" : 
                                                                         ticket.alny_marafone_tickets_status === 2 ? "Удален (отмена)" : 
                                                                         ticket.alny_marafone_tickets_status === 3 ? "Не победил" : "Неизвестно")
                                                                    )}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {formatDate(ticket.alny_marafone_tickets_time)}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {formatDate(ticket.alny_marafone_tickets_paid_time)}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {ticket.alny_marafone_tickets_for || "—"} / {ticket.alny_marafone_tickets_for_id || "—"}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                                    <button
                                                                        onClick={() => setEditingTicket(ticket)}
                                                                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                                                                        title="Редактировать"
                                                                    >
                                                                        <PencilIcon className="h-4 w-4" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* История оплат */}
                                <div>
                                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3 tracking-wide">
                                        История оплат марафона ({payments.length})
                                    </h4>
                                    {payments.length === 0 ? (
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Нет записей об оплате</p>
                                    ) : (
                                        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden border-b border-gray-200 dark:border-gray-700 sm:rounded-lg">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                                    <thead className="bg-gray-50 dark:bg-gray-900">
                                                        <tr>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Сумма</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Описание</th>
                                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Дата</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                        {payments.map((payment) => (
                                                            <tr key={payment.id}>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{payment.id}</td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-semibold text-red-600 dark:text-red-400">
                                                                    -{payment.sum} {payment.currency === 1 ? 'USDT' : 'USD'}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {payment.desc === 'Joining_Marafon' ? "Вступление в марафон" : 
                                                                     payment.desc === 'marathon_fee' ? "Ежемесячное списание" : 
                                                                     payment.desc}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                                                    {formatDate(payment.time)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={onClose}
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>

            {/* Модалки редактирования */}
            <MarathonAccountEditModal
                isOpen={!!editingAccount}
                onClose={() => setEditingAccount(null)}
                onSuccess={fetchData}
                marathonId={marathonId}
                account={editingAccount}
            />
            <MarathonTicketEditModal
                isOpen={!!editingTicket}
                onClose={() => setEditingTicket(null)}
                onSuccess={fetchData}
                marathonId={marathonId}
                ticket={editingTicket}
            />
        </div>
    );
};
